/**
 * UI object to create a progress bar ui element and bind it to the element id passed in.
 *
 * @property {string} element_id ID of element to contain the progress bar.
 * @property {boolean} hide_on_creation flag that indicates whether progress bar should be hidden of being created.
 * @property {boolean} hide_when_finished flag that indicates whether progress bar should be hidden when it reaches 100%.
 */
export class ProgressBar {
    id: string;
    container: HTMLElement;
    hide_on_creation: boolean;
    hide_when_finished: boolean;

    constructor(element_id: string, hide_on_creation: boolean = true, hide_when_finished: boolean = true) {
        this.id = this.generate_id();
        this.container = document.getElementById(element_id);
        this.hide_on_creation = hide_on_creation;
        this.hide_when_finished = hide_when_finished;
        this.init();
    }

    /**
     * Creates progress bar elements and attaches them to parent container element.
     *
     * @returns {void}
     */
    init() : void {
        const bar_container = document.createElement('div');
        bar_container.id = `progress_bar_container${this.id}`;
        bar_container.classList.add('tv-progress_bar_container');

        const bar = document.createElement('div');
        bar.id = `progress_bar${this.id}`;
        bar.classList.add('tv-progress_bar');
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-valuenow', '0');

        const progress_text = document.createElement('div');
        progress_text.id = `progress_bar_text${this.id}`;
        progress_text.classList.add('tv-progress_bar_text');

        bar_container.appendChild(bar);
        if (this.container) {
            this.container.appendChild(bar_container);
            this.container.appendChild(progress_text);
            if (this.hide_on_creation) {
                this.container.style.display = 'none';
            }
        }
    }

    /**
     * Sets parent container display property.
     *
     * @returns {void}
     */
    start() : void{
        this.container.style.display = 'block';
    }

    /**
     * Sets parent container display property to be hidden.
     *
     * @returns {void}
     */
    close() : void {
        this.container.style.display = 'none';
    }

    /**
     * Fills out progress text and progress bar to percentage passed into the function.
     *
     * @param {number} percent the percentage the progress bar should be filled.
     * @returns {void}
     */
    set_value(percent: number) : void {
        if (this.container.style.display === 'none') {
            this.start();
        }

        document.getElementById(`progress_bar${this.id}`).style.width = `${percent}%`;
        document.getElementById(`progress_bar${this.id}`).setAttribute('aria-valuenow', `${percent}`);
        document.getElementById(`progress_bar_text${this.id}`).innerHTML = `${percent}%`;
    }

    reset() : void {
        document.getElementById(`progress_bar${this.id}`).style.width = '0%';
        document.getElementById(`progress_bar_text${this.id}`).innerHTML = '';
        this.close();
    }

    /**
     * Generates a random id string to be added to the progress bar elements' ids.
     *
     * @returns {string} random id string.
     */
    generate_id() : string {
        return '_' + Math.random().toString(36).substr(2, 9);
    }
}